import React from 'react';
import { Card, Row, Col, Input, Select, InputNumber, Button, Space, Tag } from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import styles from '../../pages/admin/Subscribers.module.css';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

interface PackFiltersProps {
  filters: any;
  numberRangeFilters: {
    prices?: [number | undefined, number | undefined];
    dataValue?: [number | undefined, number | undefined];
    validityDuration?: [number | undefined, number | undefined];
  };
  setFilters: (filters: any) => void;
  setNumberRangeFilters: any;
  onClearAll: () => void;
}

const PackFilters: React.FC<PackFiltersProps> = ({
  filters,
  numberRangeFilters,
  setFilters,
  setNumberRangeFilters,
  onClearAll,
}) => {
  const { t } = useTranslation();

  const getActiveFilterCount = () => {
    const textSelectCount = Object.values(filters).filter(v => v !== undefined && v !== '').length;
    const numberCount = Object.values(numberRangeFilters).filter(([min, max]) => min !== undefined || max !== undefined).length;
    return textSelectCount + numberCount;
  };

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <FilterOutlined />
          {t('filters.title')}
          {getActiveFilterCount() > 0 && <Tag color="blue">{getActiveFilterCount()}</Tag>}
        </Space>
      }
    >
      <Row gutter={[16, 16]}>
        {/* Nom */}
        <Col xs={24} sm={12} md={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('common.name')}</label>
          <Input
            className={styles.customInput}
            placeholder={t('filters.packName')}
            value={filters.name || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
          />
        </Col>

        {/* Statut */}
        <Col xs={24} sm={12} md={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('common.status')}</label>
          <Select
            className={styles.customSelect}
            placeholder={t('filters.selectPlaceholder')}
            value={filters.status || undefined}
            onChange={(val) => setFilters(prev => ({ ...prev, status: val }))}
            allowClear
            style={{ width: '100%' }}
          >
            <Option value="available">{t('common.active')}</Option>
            <Option value="unavailable">{t('common.inactive')}</Option>
          </Select>
        </Col>

        {/* Prix */}
        <Col xs={24} sm={24} md={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('filters.priceEuro')}</label>
          <Input.Group compact>
            <InputNumber
              className={styles.customInputNumber}
              style={{ width: '50%' }}
              placeholder="Min"
              value={numberRangeFilters.prices?.[0]}
              onChange={(val) =>
                setNumberRangeFilters((prev: any) => ({ ...prev, prices: [val, prev.prices?.[1]] }))
              }
            />
            <InputNumber
              className={styles.customInputNumber}
              style={{ width: '50%' }}
              placeholder="Max"
              value={numberRangeFilters.prices?.[1]}
              onChange={(val) =>
                setNumberRangeFilters((prev: any) => ({ ...prev, prices: [prev.prices?.[0], val] }))
              }
            />
          </Input.Group>
        </Col>

        {/* Data */}
        <Col xs={24} sm={12} md={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('filters.dataGb')}</label>
          <Input.Group compact>
            <InputNumber
              className={styles.customInputNumber}
              style={{ width: '50%' }}
              placeholder="Min"
              value={numberRangeFilters.dataValue?.[0]}
              onChange={(val) =>
                setNumberRangeFilters((prev: any) => ({ ...prev, dataValue: [val, prev.dataValue?.[1]] }))
              }
            />
            <InputNumber
              className={styles.customInputNumber}
              style={{ width: '50%' }}
              placeholder="Max"
              value={numberRangeFilters.dataValue?.[1]}
              onChange={(val) =>
                setNumberRangeFilters((prev: any) => ({ ...prev, dataValue: [prev.dataValue?.[0], val] }))
              }
            />
          </Input.Group>
        </Col>

        {/* Durée */}
        <Col xs={24} sm={12} md={8}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('filters.durationDays')}</label>
          <Input.Group compact>
            <InputNumber
              className={styles.customInputNumber}
              style={{ width: '50%' }}
              placeholder="Min"
              value={numberRangeFilters.validityDuration?.[0]}
              onChange={(val) =>
                setNumberRangeFilters((prev: any) => ({ ...prev, validityDuration: [val, prev.validityDuration?.[1]] }))
              }
            />
            <InputNumber
              className={styles.customInputNumber}
              style={{ width: '50%' }}
              placeholder="Max"
              value={numberRangeFilters.validityDuration?.[1]}
              onChange={(val) =>
                setNumberRangeFilters((prev: any) => ({ ...prev, validityDuration: [prev.validityDuration?.[0], val] }))
              }
            />
          </Input.Group>
        </Col>

        {/* Bouton Réinitialiser */}
        <Col xs={24} sm={24} md={8}>
          <div style={{ paddingTop: 24 }}>
            <Space>
              <Button icon={<ClearOutlined />} onClick={onClearAll} disabled={getActiveFilterCount() === 0}>
                {t('filters.reset')}
              </Button>
              {getActiveFilterCount() > 0 && <Tag color="blue">{getActiveFilterCount()} {t('filters.activeFilters')}</Tag>}
            </Space>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default PackFilters;
